import { useEffect, useId, useState } from 'react';
import { useT, type MessageKey } from '../../i18n';
import { listClubMembers, type ClubMember } from '../../lib/api/clubMembers';
import {
  sendPaymentLink,
  withdrawPaymentLink,
  type SendPaymentLinkResult,
  type WithdrawPaymentLinkResult,
} from '../../lib/api/paymentLink';

// ─────────────────────────────────────────────────────────────────────
// «SEND BETALINGSLENKE» ON A PRICED STAFF BOOKING (#1137).
//
// Owner decision 2026-11-02: a staff-made priced booking is payable in the
// product — the member it is for gets a link and pays through the ordinary
// Stripe flow in the member app. Staff pick the member from the club's own
// roster (`GET /members/roster`); the server decides everything else — whether
// the booking can be linked, what it costs, who may ask — and answers with a
// code this screen turns into a sentence.
//
// Shown only where it can mean something: an EXISTING booking that reads
// `pending`. The server still refuses what this cannot see (a member's own
// booking inside its fifteen minutes, a price that differs from the zones'),
// and each refusal has its own sentence.
//
// «TREKK TILBAKE BETALINGSLENKE» (#1310, owner decision 2026-11-03) sits
// beside it: the booking goes back to the club, any open payment cancelled at
// Stripe first. Offered on every such booking — the staff calendar does not
// carry who a booking belongs to — and a booking with no link answers
// `not_linked`, which has its own sentence.
// ─────────────────────────────────────────────────────────────────────

/** The server's refusal codes this screen has a sentence for. */
const REFUSAL_KEY: Record<string, MessageKey> = {
  payments_unavailable: 'booking.paymentLink.error.payments_unavailable',
  not_on_roster: 'booking.paymentLink.error.not_on_roster',
  player_has_no_account: 'booking.paymentLink.error.player_has_no_account',
  booking_not_found: 'booking.paymentLink.error.booking_not_found',
  already_paid: 'booking.paymentLink.error.already_paid',
  nothing_to_pay: 'booking.paymentLink.error.nothing_to_pay',
  booking_not_linkable: 'booking.paymentLink.error.booking_not_linkable',
  booking_linked_to_another_player: 'booking.paymentLink.error.booking_linked_to_another_player',
  member_payment_window: 'booking.paymentLink.error.member_payment_window',
  organization_not_connected: 'booking.paymentLink.error.organization_not_connected',
  unpriceable: 'booking.paymentLink.error.unpriceable',
  price_mismatch: 'booking.paymentLink.error.price_mismatch',
  equipment_not_payable: 'booking.paymentLink.error.equipment_not_payable',
  awaiting_verification: 'booking.paymentLink.error.awaiting_verification',
  reservation_not_payable: 'booking.paymentLink.error.reservation_not_payable',
  state_moved: 'booking.paymentLink.error.state_moved',
  forbidden: 'booking.paymentLink.error.forbidden',
  // «Trekk tilbake» (#1310).
  not_linked: 'booking.paymentLink.error.not_linked',
  payment_unconfirmed: 'booking.paymentLink.error.payment_unconfirmed',
  payment_open: 'booking.paymentLink.error.payment_open',
};

type Members =
  { state: 'loading' } | { state: 'ok'; members: ClubMember[] } | { state: 'unavailable' };

export interface SendPaymentLinkProps {
  /** The booking's `book_…` id, as the calendar holds it. */
  bookingId: string;
  /** Injectable for tests; the real calls by default. */
  loadMembers?: typeof listClubMembers;
  send?: typeof sendPaymentLink;
  withdraw?: typeof withdrawPaymentLink;
}

export function SendPaymentLink({
  bookingId,
  loadMembers = listClubMembers,
  send = sendPaymentLink,
  withdraw = withdrawPaymentLink,
}: SendPaymentLinkProps) {
  const t = useT();
  const selectId = useId();
  const [members, setMembers] = useState<Members>({ state: 'loading' });
  const [chosen, setChosen] = useState('');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<SendPaymentLinkResult | null>(null);
  const [withdrawn, setWithdrawn] = useState<WithdrawPaymentLinkResult | null>(null);

  useEffect(() => {
    let live = true;
    void loadMembers().then((r) => {
      if (!live) return;
      setMembers(
        r.status === 'ok' ? { state: 'ok', members: r.members } : { state: 'unavailable' },
      );
    });
    return () => {
      live = false;
    };
  }, [loadMembers]);

  const submit = async () => {
    if (chosen === '' || busy) return;
    setBusy(true);
    setResult(null);
    setWithdrawn(null);
    try {
      setResult(await send(bookingId, chosen));
    } finally {
      setBusy(false);
    }
  };

  const takeBack = async () => {
    if (busy) return;
    setBusy(true);
    setResult(null);
    setWithdrawn(null);
    try {
      setWithdrawn(await withdraw(bookingId));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="mt-2 rounded border border-line p-2 space-y-1.5"
      data-testid="send-payment-link"
    >
      <div className="text-[10px] font-mono uppercase tracking-wider text-ink-3">
        {t('booking.paymentLink.title')}
      </div>
      <p className="text-[11px] text-ink-3">{t('booking.paymentLink.help')}</p>
      {members.state === 'loading' && (
        <p className="text-[11px] text-ink-3">{t('booking.paymentLink.loadingMembers')}</p>
      )}
      {members.state === 'unavailable' && (
        <p className="text-[11px] text-ink-2">{t('booking.paymentLink.membersUnavailable')}</p>
      )}
      {members.state === 'ok' && members.members.length === 0 && (
        <p className="text-[11px] text-ink-2">{t('booking.paymentLink.noMembers')}</p>
      )}
      {members.state === 'ok' && members.members.length > 0 && (
        <div className="flex gap-1.5 items-end">
          <div className="flex-1">
            <label
              htmlFor={selectId}
              className="block text-[10px] font-mono uppercase tracking-wider text-ink-3 mb-1"
            >
              {t('booking.paymentLink.member')}
            </label>
            <select
              id={selectId}
              className="input w-full"
              value={chosen}
              onChange={(e) => {
                setChosen(e.target.value);
                setResult(null);
              }}
            >
              <option value="">{t('booking.paymentLink.choose')}</option>
              {members.members.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.name}
                </option>
              ))}
            </select>
          </div>
          <button
            type="button"
            onClick={() => void submit()}
            disabled={chosen === '' || busy}
            className="text-xs font-semibold px-3 py-2 rounded border-2 border-line text-ink-2 hover:border-ink-3 disabled:opacity-50"
          >
            {busy ? t('booking.paymentLink.sending') : t('booking.paymentLink.send')}
          </button>
        </div>
      )}
      <button
        type="button"
        onClick={() => void takeBack()}
        disabled={busy}
        className="text-[11px] font-semibold px-2 py-1 rounded border border-line text-ink-2 hover:border-ink-3 disabled:opacity-50"
      >
        {busy ? t('booking.paymentLink.withdrawing') : t('booking.paymentLink.withdraw')}
      </button>
      <div role="status" aria-live="polite" className="text-[11px]">
        {result && <ResultLine result={result} />}
        {withdrawn && <WithdrawLine result={withdrawn} />}
      </div>
    </div>
  );
}

function WithdrawLine({ result }: { result: WithdrawPaymentLinkResult }) {
  const t = useT();
  if (result.state === 'withdrawn') {
    return (
      <span className="text-emerald-700">
        {t(
          result.attemptCancelled
            ? 'booking.paymentLink.withdrawnCancelled'
            : 'booking.paymentLink.withdrawn',
        )}
      </span>
    );
  }
  if (result.state === 'refused') {
    const key = REFUSAL_KEY[result.code];
    if (!key) console.warn('[payment link] unmapped withdraw code:', result.code);
    return (
      <span className="text-red-700">{t(key ?? 'booking.paymentLink.error.withdrawUnknown')}</span>
    );
  }
  return <span className="text-red-700">{t('booking.paymentLink.error.withdrawUnknown')}</span>;
}

function ResultLine({ result }: { result: SendPaymentLinkResult }) {
  const t = useT();
  if (result.state === 'linked') {
    const vars = { name: result.playerName, amount: String(Math.round(result.amountMinor / 100)) };
    return result.mail === 'sent' ? (
      <span className="text-emerald-700">{t('booking.paymentLink.sent', vars)}</span>
    ) : (
      <span className="text-ink-2">{t('booking.paymentLink.linkedNoMail', vars)}</span>
    );
  }
  if (result.state === 'refused') {
    const key = REFUSAL_KEY[result.code];
    if (!key) console.warn('[payment link] unmapped refusal code:', result.code);
    return <span className="text-red-700">{t(key ?? 'booking.paymentLink.error.unknown')}</span>;
  }
  return <span className="text-red-700">{t('booking.paymentLink.error.unknown')}</span>;
}
